import { useAuth } from '../contexts/AuthContext';
import { useClient } from '../contexts/ClientContext';
import { useWebSocketChat } from './useWebSocketChat';
import { useEffect, useState, useCallback } from 'react';

export function useAgentClientChat(opts: { autoConnect?: boolean } = {}) {
  const { user, isAuthenticated } = useAuth();
  const { agent } = useClient();

  // Initialise the websocket chat hook
  const wsChat = useWebSocketChat({
    autoConnect: opts.autoConnect,
    buildUrl: () => {
      if (!isAuthenticated) return null;
      return new URL('/api/chat/agent', window.location.origin)
        .toString()
        .replace(/^http/, 'ws');
    },
    currentUser: {
      id: user?.id,
      name: user?.name ?? 'You',
      email: user?.email,
      picture: user?.picture,
      role: 'client'
    },
    otherUser: {
      name: agent?.name ?? 'Your Agent',
      email: agent?.email,
      picture: agent?.picture,
      role: 'agent'
    },
    seedMessages: [
      {
        id: '1',
        senderId: agent?.email || 'agent@example.com',
        senderName: agent?.name || 'Your Agent',
        senderType: 'agent',
        message: 'Hi! Welcome to CribCritic. I\'m here to help you with your property search and analysis. How can I assist you today?',
        timestamp: '2024-01-22T09:00:00Z',
        isRead: true
      }
    ]      // existing dev seeds
  });

  // ─────────────────────────────────────────────
  // Fetch initial unread-message count (HTTP GET)
  const [serverUnread, setServerUnread] = useState(0);

  useEffect(() => {
    if (!isAuthenticated) return;
    const ctrl = new AbortController();

    (async () => {
      try {
        const res  = await fetch('/api/chat/agent', { signal: ctrl.signal });
        const json = await res.json();
        if (json.success && typeof json.unreadMessages === 'number') {
          setServerUnread(json.unreadMessages);
        }
      } catch (err) {
        if (!ctrl.signal.aborted) {
          console.error('Failed to get unread messages', err);
        }
      }
    })();

    return () => ctrl.abort();
  }, [isAuthenticated]);

  // Use websocket value once available
  const effectiveUnread = wsChat.unreadCount !== 0 ? wsChat.unreadCount : serverUnread;

  // Wrap markAsRead to clear local count, too
  const markAsRead = useCallback(async () => {
    const res = await wsChat.markAsRead();
    setServerUnread(0);
    return res;
  }, [wsChat.markAsRead]);

  return {
    ...wsChat,
    unreadCount: effectiveUnread,
    markAsRead
  };
}